import axios from 'axios';

import { getApiBaseUrl } from '../config/ApiConfig';
import { getMidwayJwtToken } from '../auth/MidwayJwtToken';
import type { ViewType } from '../views/types';

export type { ViewType };

export type Granularity = 'aggregated' | 'detailed';

export interface QueryRequest {
  region: string;
  business: string;
  viewType: ViewType;
  fgids?: number[];
  siteIds?: string[];
  asins?: string[];
  startDate?: string;
  endDate?: string;
  granularity?: Granularity;
  leadTime?: number;
  targetDates?: string[];
  fcdStartDate?: string;
  fcdEndDate?: string;
}

export interface QueryMetadata {
  fgids: number[];
  siteIds: string[];
  viewType: ViewType;
  granularity?: Granularity;
  business: string;
  region: string;
  awsRegion: string;
  // Resolved during FGDS lookup; used to enrich rows with the counterpart column.
  fgidSiteMapping?: Record<string, string>;
  siteFgidMapping?: Record<string, number>;
}

export interface QueryResult {
  data: ResultRow[];
  fgidSiteMapping?: Record<string, string>;
  siteFgidMapping?: Record<string, number>;
  queryId: string;
  awsRegion: string;
}

export interface StartQueryResponse {
  queryId: string;
  status: string;
  metadata: QueryMetadata;
}

export type ResultRow = Record<string, string | number>;

export interface ResultsResponse {
  status: 'running' | 'succeeded' | 'failed';
  viewType?: ViewType;
  data?: ResultRow[];
  message?: string;
  error?: string;
  fgidSiteMapping?: Record<string, string>;
  siteFgidMapping?: Record<string, number>;
}

async function authHeader(): Promise<Record<string, string>> {
  // Skip auth on localhost — local backend doesn't require it
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return {};
  }
  const token = await getMidwayJwtToken();
  return { Authorization: `Bearer ${token}` };
}

export async function startQuery(
  request: QueryRequest,
  signal?: AbortSignal
): Promise<StartQueryResponse> {
  const response = await axios.post<StartQueryResponse>(
    `${getApiBaseUrl()}/query`,
    request,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

export async function getResults(
  queryId: string,
  awsRegion: string,
  viewType: ViewType,
  signal?: AbortSignal
): Promise<ResultsResponse> {
  const response = await axios.get<ResultsResponse>(
    `${getApiBaseUrl()}/results/${encodeURIComponent(queryId)}`,
    {
      params: { awsRegion, viewType },
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Cache only a small pointer (queryId) in localStorage, not the result data;
// the data stays in S3 behind GET /results.
const CACHE_PREFIX = 'fq:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedQuery {
  queryId: string;
  awsRegion: string;
  viewType: string;
  fgidSiteMapping?: Record<string, string>;
  siteFgidMapping?: Record<string, number>;
  timestamp: number;
}

// Sorted keys make the key independent of property insertion order.
export function paramsKey(request: QueryRequest): string {
  return CACHE_PREFIX + JSON.stringify(request, Object.keys(request).sort());
}

function getCachedQueryId(request: QueryRequest): CachedQuery | null {
  try {
    const raw = localStorage.getItem(paramsKey(request));
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedQuery;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(paramsKey(request));
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function setCachedQueryId(request: QueryRequest, entry: Omit<CachedQuery, 'timestamp'>): void {
  try {
    localStorage.setItem(
      paramsKey(request),
      JSON.stringify({ ...entry, timestamp: Date.now() })
    );
  } catch {
    // best-effort
  }
}

// Shared links carry only the queryId, so look the mappings up by scanning the
// cached entries (available on the same browser that ran the query).
export function getCachedMappingsByQueryId(queryId: string): Pick<
  CachedQuery,
  'fgidSiteMapping' | 'siteFgidMapping'
> | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(CACHE_PREFIX)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const cached = JSON.parse(raw) as CachedQuery;
      if (cached.queryId === queryId) {
        return {
          fgidSiteMapping: cached.fgidSiteMapping,
          siteFgidMapping: cached.siteFgidMapping,
        };
      }
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Starts a query and polls until it succeeds or fails. Returns the succeeded
 * results, or throws with the failure reason. Pass an AbortSignal to cancel an
 * in-flight poll loop (e.g. when a newer query is started).
 */
export async function runQuery(
  request: QueryRequest,
  signal?: AbortSignal
): Promise<QueryResult> {
  // Cached queryId: fetch stored results directly, skipping POST /query.
  const cached = getCachedQueryId(request);
  if (cached) {
    try {
      const results = await getResults(
        cached.queryId,
        cached.awsRegion,
        cached.viewType as ViewType,
        signal
      );
      if (results.status === 'succeeded') {
        return {
          data: results.data ?? [],
          fgidSiteMapping: cached.fgidSiteMapping,
          siteFgidMapping: cached.siteFgidMapping,
          queryId: cached.queryId,
          awsRegion: cached.awsRegion,
        };
      }
    } catch (e) {
      if (signal?.aborted) throw e; // respect intentional cancellation
      // getResults failed (e.g. stored results 404'd) — fall through to re-run.
    }
    // Stale/failed pointer: drop it and start a fresh query below.
    try {
      localStorage.removeItem(paramsKey(request));
    } catch {
      // ignore
    }
  }

  const { queryId, metadata } = await startQuery(request, signal);
  const { fgidSiteMapping, siteFgidMapping } = metadata;
  setCachedQueryId(request, {
    queryId,
    awsRegion: metadata.awsRegion,
    viewType: request.viewType,
    fgidSiteMapping,
    siteFgidMapping,
  });

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException('Query cancelled', 'AbortError');
    }
    const results = await getResults(
      queryId,
      metadata.awsRegion,
      request.viewType,
      signal
    );
    if (results.status === 'succeeded') {
      return {
        data: results.data ?? [],
        fgidSiteMapping,
        siteFgidMapping,
        queryId,
        awsRegion: metadata.awsRegion,
      };
    }
    if (results.status === 'failed') {
      throw new Error(results.error || 'Query failed');
    }
    await delay(POLL_INTERVAL_MS);
  }

  throw new Error('Query timed out while waiting for results');
}
